export interface TutorialContentBlock {
  type: 'prose' | 'code'
  text: string
}

export interface TutorialSection {
  id: string
  title: string
  intro?: string
  /**
   * The primary code block for this section (kept for backwards compatibility).
   * When multiple code blocks exist, this is always the first one.
   */
  code?: string
  explanation?: string
  /**
   * All code blocks that appear in this section, in order.
   * The first entry always equals `code`.
   */
  codes?: string[]
  /**
   * Ordered content blocks (prose and code) for this section.
   * Used to render prose and players inline in the tutorial view.
   */
  blocks?: TutorialContentBlock[]
}

export interface ParsedTutorial {
  title: string
  intro?: string
  sections: TutorialSection[]
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function parseMarkdown(markdown: string): ParsedTutorial {
  const lines = markdown.split('\n')
  const result: ParsedTutorial = {
    title: '',
    sections: [],
  }

  let currentSection: TutorialSection | null = null
  let currentH2Section: TutorialSection | null = null
  let currentH3Section: TutorialSection | null = null
  let codeBlockBuffer: string[] = []
  let inCodeBlock = false
  let codeBlockLanguage = ''
  let currentBlock: 'intro' | null = null
  let currentSectionProseLines: string[] = []

  const flushSectionProse = () => {
    if (!currentSection) return
    if (currentSectionProseLines.length === 0) return
    const text = currentSectionProseLines.join('\n').trim()
    if (!text) {
      currentSectionProseLines = []
      return
    }
    if (!currentSection.blocks) {
      currentSection.blocks = []
    }
    currentSection.blocks.push({ type: 'prose', text })
    currentSectionProseLines = []
  }

  const addCodeBlockToSection = (code: string) => {
    if (!currentSection) return
    if (!currentSection.blocks) {
      currentSection.blocks = []
    }
    if (!currentSection.codes) {
      currentSection.codes = []
    }
    currentSection.blocks.push({ type: 'code', text: code })
    currentSection.codes.push(code)
    if (!currentSection.code) {
      currentSection.code = code
    }
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    const trimmedLine = line.trim()

    if (trimmedLine.startsWith('```')) {
      if (!inCodeBlock) {
        // Starting a new code block - flush any prose collected so far
        flushSectionProse()
      }

      if (inCodeBlock) {
        if (codeBlockBuffer.length > 0) {
          if (currentSection) {
            const code = codeBlockBuffer.join('\n')
            addCodeBlockToSection(code)
          }
          codeBlockBuffer = []
        }
        inCodeBlock = false
        codeBlockLanguage = ''
      }
      else {
        inCodeBlock = true
        codeBlockLanguage = trimmedLine.slice(3).trim()
      }
      continue
    }

    if (inCodeBlock) {
      codeBlockBuffer.push(line)
      continue
    }

    if (trimmedLine.startsWith('# ')) {
      result.title = trimmedLine.slice(2).trim()
      currentSection = null
      currentH2Section = null
      currentH3Section = null
      currentBlock = 'intro'
      continue
    }

    if (trimmedLine.startsWith('## ')) {
      if (currentSection) {
        flushSectionProse()
        result.sections.push(currentSection)
      }

      const title = trimmedLine.slice(3).trim()
      const id = slugify(title)
      currentH2Section = {
        id,
        title,
      }
      currentH3Section = null
      currentSection = currentH2Section
      currentSectionProseLines = []
      continue
    }

    if (trimmedLine.startsWith('### ')) {
      if (currentSection) {
        flushSectionProse()
        result.sections.push(currentSection)
      }

      const title = trimmedLine.slice(4).trim()
      const parentTitle = currentH2Section ? currentH2Section.title : ''
      const fullTitle = parentTitle ? `${parentTitle}: ${title}` : title
      const id = slugify(fullTitle)
      currentH3Section = {
        id,
        title, // Only the subsection title, not the full title
      }
      currentSection = currentH3Section
      currentSectionProseLines = []
      continue
    }

    if (trimmedLine.startsWith('#### ')) {
      if (currentSection) {
        flushSectionProse()
        result.sections.push(currentSection)
      }

      const title = trimmedLine.slice(5).trim()
      const h2Title = currentH2Section ? currentH2Section.title : ''
      const h3Title = currentH3Section ? currentH3Section.title : ''
      const parentTitle = h3Title ? `${h2Title}: ${h3Title}` : h2Title || ''
      const fullTitle = parentTitle ? `${parentTitle}: ${title}` : title
      const id = slugify(fullTitle)
      currentSection = {
        id,
        title, // Only the H4 title, not the full title
      }
      currentSectionProseLines = []
      continue
    }

    if (trimmedLine === '') {
      if (inCodeBlock && codeBlockBuffer.length > 0 && currentSection) {
        const code = codeBlockBuffer.join('\n')
        addCodeBlockToSection(code)
        codeBlockBuffer = []
        inCodeBlock = false
      }
      else if (currentSection) {
        currentSectionProseLines.push('')
      }
      continue
    }

    if (currentSection) {
      currentSectionProseLines.push(line)
    }
    else if (currentBlock === 'intro' && !inCodeBlock) {
      if (!result.intro) {
        result.intro = ''
      }
      else {
        result.intro += '\n'
      }
      result.intro += line
    }
  }

  if (currentSection) {
    if (inCodeBlock && codeBlockBuffer.length > 0) {
      const code = codeBlockBuffer.join('\n')
      addCodeBlockToSection(code)
    }
    flushSectionProse()
    result.sections.push(currentSection)
  }

  if (result.intro) {
    result.intro = result.intro.trim()
  }

  // Derive legacy intro/explanation fields from blocks for backwards compatibility
  for (const section of result.sections) {
    if (section.blocks && section.blocks.length > 0) {
      const proseBlocks = section.blocks.filter(b => b.type === 'prose')
      if (proseBlocks.length > 0) {
        section.intro = proseBlocks[0].text.trim()
        if (proseBlocks.length > 1) {
          section.explanation = proseBlocks
            .slice(1)
            .map(b => b.text.trim())
            .filter(Boolean)
            .join('\n\n')
            || undefined
        }
      }
    }
    if (section.intro) {
      section.intro = section.intro.trim()
    }
    if (section.explanation) {
      section.explanation = section.explanation.trim()
    }
  }

  return result
}

export function parseTutorialMarkdown(markdown: string): ParsedTutorial {
  return parseMarkdown(markdown)
}

